/**
 * A wrapper around performance API to mark begin and end times
 * @module
 */

var actionTypes = require('../constants/action_types');
var createError = require('../utils/custom_error').create;
var DateEnv = require('./date');
var Flux = require('../flux');
var WindowEnv = require('./window');

var di = require('core/di');
var RumStore = di.get('stores/rum');

var namespace = 'optimizely:';
var PerformanceError = exports.Error = createError('PerformanceError');

/**
 * Begins measuring timing
 *
 * Defines a performance.mark called "${namespace}:${markName}Begin"
 *
 * @param {string} markName name
 */
exports.time = function(markName:string) {
  if (__PROFILE__ || RumStore.getSampleRum() || __TEST__) {
    var performanceEnv = getPerformanceAPI();
    if (performanceEnv && performanceEnv.mark) {
      var perfMarkName = namespace + markName;
      performanceEnv.clearMarks(perfMarkName + 'Begin');
      performanceEnv.mark(perfMarkName + 'Begin');
    }
  }
};


/**
 * Ends measuring timing
 *
 * Defines a performance.mark called "${namespace"}:${markName}End"
 *
 * Stores start/end in performance store
 *
 * If logging at info level or above:
 *  Create a measure for "${namespace}:${markName}End" - "${namespace}:${markName}Begin" and called "${markName}Time"
 * else:
 *  Clear Begin/End marks
 *
 * @param {string} markName name
 */
 exports.timeEnd = function(markName:string) {
  if (__PROFILE__ || RumStore.getSampleRum() || __TEST__) {
    var performanceEnv = getPerformanceAPI();
    if (performanceEnv && performanceEnv.mark) {
      var perfMarkName = namespace + markName;

      var beginMarks = performanceEnv.getEntriesByName(perfMarkName + 'Begin');
      if (beginMarks.length === 0) {
        throw new PerformanceError('Called timeEnd without matching time: ' + markName);
      }

      performanceEnv.clearMarks(perfMarkName + 'End');
      performanceEnv.mark(perfMarkName + 'End');
      var endMarks = performanceEnv.getEntriesByName(perfMarkName + 'End');

      var measureName = markName + 'Time';
      //Cannot use measure because it is unpredictable as to the location of the measure in the array
      var duration = endMarks[0].startTime - beginMarks[0].startTime;
      Flux.dispatch(actionTypes.SET_PERFORMANCE_MARKS_DATA, {
        name: measureName,
        data: {
          startTime: Math.round(beginMarks[0].startTime * 1e3) / 1e3,
          duration: Math.round(duration * 1e3) / 1e3,
        },
      });

      if (__PROFILE__) {
        var perfMeasureName = namespace + measureName;
        performanceEnv.measure(perfMeasureName, perfMarkName + 'Begin', perfMarkName + 'End');
      }
    }
  }
};

/**
 * Set a mark, with a specified startTime and duration.
 *
 * Defines a performance.mark called "${namespace"}:${markName}"
 * Stores the mark in the performance store
 *
 * @param {string} markName name
 * @param {number} startTime The startTime of the mark
 * @param {number} duration The duration of the mark
 */
 exports.setMark = function(markName:string, startTime:number, duration:number) {
  if (__PROFILE__ || RumStore.getSampleRum() || __TEST__) {
    var performanceEnv = getPerformanceAPI();
    if (performanceEnv && performanceEnv.mark) {
      var perfMarkName = namespace + markName;
      performanceEnv.mark(perfMarkName);

      Flux.dispatch(actionTypes.SET_PERFORMANCE_MARKS_DATA, {
        name: markName,
        data: {
          startTime: Math.round(startTime * 1e3) / 1e3,
          duration: Math.round(duration * 1e3) / 1e3,
        },
      });
    }
  }
};

/**
 * Return performance.now timestamp
 * @return {number}
 */
exports.now = function(): number {
  var performanceEnv = getPerformanceAPI();
  if(performanceEnv) {
    return performanceEnv.now();
  } else {
    return DateEnv.now();
  }
};


function getPerformanceAPI() {
  return WindowEnv.getGlobal('performance');
}


if (__TEST__) {
  exports.namespace = namespace;
}
